import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Shield, Trash2, Edit, Key, RefreshCw, Plus, Eye, UserCheck, Eraser } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalMessages: 0,
    recentUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'user'
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [newPassword, setNewPassword] = useState('');
  const [userDetail, setUserDetail] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Gagal mengambil data users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Gagal mengambil detail user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setEditDialogOpen(true);
  };


  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Semua field harus diisi');
      return;
    }

    if (createForm.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users`,
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User berhasil dibuat');
      setCreateDialogOpen(false);
      setCreateForm({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User berhasil diupdate');
      setEditDialogOpen(false);
      fetchUsers();
      fetchUserDetail(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update user');
    }
  };

  const handleCleanDB = async (user) => {
    try {
      await axios.post(
        `${API}/admin/clean-db`,
        {"user_id": user.id},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User berhasil diclean');
      // setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal Eraser user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password berhasil direset');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Gagal reset password');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus user ${user.name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User berhasil dihapus');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 flex items-center gap-3">
            <Shield className="text-green-600" size={40} />
            User Management
          </h1>
          <p className="text-gray-600 mt-2">Kelola semua user di platform</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="create-user-button"
            onClick={() => setCreateDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="mr-2" size={18} />
            Create User
          </Button>
          <Button
            data-testid="refresh-users-button"
            onClick={() => {
              fetchUsers();
              fetchStats();
            }}
            className="bg-green-600 hover:bg-green-700"
          >
            <RefreshCw className="mr-2" size={18} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Users</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalUsers}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="text-blue-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Active Users</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.activeUsers}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <UserCheck className="text-green-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Messages</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalMessages}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Plus className="text-purple-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">New (7 days)</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.recentUsers}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <RefreshCw className="text-orange-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="text-green-600" size={24} />
            All Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold">Name</th>
                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                    <th className="text-left py-3 px-4 font-semibold">Role</th>
                    <th className="text-left py-3 px-4 font-semibold">Messages</th>
                    <th className="text-left py-3 px-4 font-semibold">WhatsApp</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                      <td className="py-3 px-4">{user.name}</td>
                      <td className="py-3 px-4">{user.email}</td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={user.role === 'superadmin' ? 'default' : 'secondary'}
                          className={user.role === 'superadmin' ? 'bg-purple-600' : ''}
                        >
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">{user.totalMessages || 0}</td>
                      <td className="py-3 px-4">
                        <Badge variant={user.whatsappConnected ? 'default' : 'secondary'}
                          className={user.whatsappConnected ? 'bg-green-600' : ''}>
                          {user.whatsappConnected ? 'Connected' : 'Disconnected'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fetchUserDetail(user.id)}
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              setResetPasswordDialogOpen(true);
                            }}
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key size={16} />
                          </Button>
                          {user.role !== 'superadmin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleDeleteUser(user)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleCleanDB(user)}
                            data-testid={`clear-user-${user.id}`}
                          >
                            <Eraser size={16} />
                          </Button>
                          
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                data-testid="create-name-input"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                data-testid="create-email-input"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                data-testid="create-password-input"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Minimal 6 karakter"
              />
            </div>
            <div>
              <Label htmlFor="create-role">Role</Label>
              <Select value={createForm.role} onValueChange={(value) => setCreateForm({ ...createForm, role: value })}>
                <SelectTrigger data-testid="create-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setCreateForm({ name: '', email: '', password: '', role: 'user' });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-create-user"
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editForm.role} onValueChange={(value) => setEditForm({ ...editForm, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} className="bg-green-600 hover:bg-green-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimal 6 karakter"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordDialogOpen(false);
              setNewPassword('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-green-600 hover:bg-green-700">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {userDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <p className="font-semibold">{userDetail.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-semibold">{userDetail.email}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Role</Label>
                  <Badge variant={userDetail.role === 'superadmin' ? 'default' : 'secondary'}
                    className={userDetail.role === 'superadmin' ? 'bg-purple-600' : ''}>
                    {userDetail.role}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-600">WhatsApp Status</Label>
                  <Badge variant={userDetail.whatsappConnected ? 'default' : 'secondary'}
                    className={userDetail.whatsappConnected ? 'bg-green-600' : ''}>
                    {userDetail.whatsappConnected ? 'Connected' : 'Disconnected'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-600">API Key</Label>
                <Input value={userDetail.apiKey} disabled className="font-mono text-sm" />
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{userDetail.stats?.totalMessages || 0}</p>
                  <p className="text-sm text-gray-600">Total Messages</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{userDetail.stats?.sentMessages || 0}</p>
                  <p className="text-sm text-gray-600">Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{userDetail.stats?.receivedMessages || 0}</p>
                  <p className="text-sm text-gray-600">Received</p>
                </div>
              </div>

              {userDetail.webhookUrl && (
                <div>
                  <Label className="text-gray-600">Webhook URL</Label>
                  <Input value={userDetail.webhookUrl} disabled />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {userDetail && (
              <Button
                variant="outline"
                onClick={() => handleEditUser(userDetail)}
                data-testid={`edit2-user-${userDetail.id}`}
              >
                <Edit size={16} /> Edit
              </Button>
            )}
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
